import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const WELCOME_EMAIL_KEY = "ff_welcome_sent";

async function triggerWelcomeEmail(user: User) {
  // Only trigger once per user per device, and only for confirmed emails
  if (!user.email_confirmed_at) return;
  const sentKey = `${WELCOME_EMAIL_KEY}_${user.id}`;
  if (localStorage.getItem(sentKey)) return;

  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    // Pass userId so the edge function can look up first_name from profiles table
    await fetch(
      `https://${projectId}.supabase.co/functions/v1/send-welcome-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ email: user.email, userId: user.id }),
      }
    );
    localStorage.setItem(sentKey, "1");
  } catch (err) {
    // Non-blocking — welcome email failure should never break the app
    console.warn("Welcome email could not be sent:", err);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Fire welcome email on first confirmed sign-in
        if (
          (event === "SIGNED_IN" || event === "USER_UPDATED") &&
          session?.user?.email_confirmed_at
        ) {
          triggerWelcomeEmail(session.user);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
